import type Competence from "constants/Competence";
import competenceMap from "constants/CompetenceMap";
import type { Metier, PerimetreWithoutTerritoire, Poste } from "models";

function getSortAnythingByCompetence<T>(
  f: (t: T) => Competence | undefined = (t) => t as Competence | undefined
): (a: T, b: T) => number {
  return (a, b) =>
    (competenceMap.get(f(a))?.index ?? Number.MAX_SAFE_INTEGER) -
    (competenceMap.get(f(b))?.index ?? Number.MAX_SAFE_INTEGER);
}

function sortCompetences(a: Competence, b: Competence): number {
  return getSortAnythingByCompetence()(a, b);
}

function sortPerimetres(a: PerimetreWithoutTerritoire, b: PerimetreWithoutTerritoire): number {
  const sortedByComp = sortPerimetresByCompetence(a, b);
  return sortedByComp === 0 ? sortPerimetresById(a, b) : sortedByComp;
}

function sortPerimetresById(a: PerimetreWithoutTerritoire, b: PerimetreWithoutTerritoire): number {
  return a.code.localeCompare(b.code, undefined, { numeric: true });
}

function sortPerimetresByCompetence(
  a: PerimetreWithoutTerritoire,
  b: PerimetreWithoutTerritoire
): number {
  return getSortAnythingByCompetence(
    (perimetre: PerimetreWithoutTerritoire) => perimetre.competence
  )(a, b);
}

function sortMetiers(a: Metier, b: Metier): number {
  const sortByComp = sortMetiersByCompetence(a, b);
  return sortByComp === 0 ? sortAnyByLibelle(a, b) : sortByComp;
}

function sortMetiersByCompetence(a: Metier, b: Metier): number {
  return getSortAnythingByCompetence((metier: Metier) => metier?.competence)(a, b);
}

function sortPostes(a: Poste, b: Poste): number {
  return a.codeAstre.localeCompare(b.codeAstre, undefined, { numeric: true });
}

interface ObjectWithLibelle {
  libelle: string;
}

function sortAnyByLibelle(
  { libelle: a }: ObjectWithLibelle,
  { libelle: b }: ObjectWithLibelle
): number {
  return a.localeCompare(b);
}

export const sortingUtil = {
  sortAnyByLibelle,
  sortCommunes: sortAnyByLibelle,
  sortCompetences,
  sortMetiers,
  sortPerimetres,
  sortPostes,
  sortTerritoires: sortAnyByLibelle,
};
